import { ApiError } from '../types'

/**
 * Putting a file on a presigned link, and trying again when the network drops it.
 *
 * **This is the one request in the product that does not go through our API.**
 * It goes straight to storage on a link that carries its own authorisation, so
 * it never had the retry, the token refresh or the error wording that every
 * other call has. It had a bare `fetch` and nothing else.
 *
 * When `fetch` fails at the network level it throws `TypeError: Failed to
 * fetch`, and that string went to the customer unchanged. Pressing the button
 * again worked, because the blip had passed, which is what made it look like
 * the feature was broken at random rather than that one request had failed.
 *
 * Two attempts to this, then one more. A transient drop survives it and the
 * person never learns it happened.
 */

/** How many goes in total, and how long to wait between them. */
const TRIES = 3
const WAITS = [400, 1200]

/** Long enough that no honest upload reaches it, short enough that a dead connection does. */
const GIVE_UP_AFTER = 90_000

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms))

/**
 * Whether trying again could plausibly work.
 *
 * A refused signature or a rejected request will be refused identically the
 * second time, and retrying it only makes the person wait longer to read the
 * same message. A dropped connection or a storage error might not be there a
 * moment later.
 */
const worthRetrying = (status: number | null) =>
  // No status at all means the request never got an answer: a dropped
  // connection, a dead link, a browser giving up.
  status === null || status === 429 || status >= 500

/**
 * What to tell somebody, in words they can act on.
 *
 * "Failed to fetch" is the browser talking to itself. It names no cause a
 * person could do anything about and it does not say whether to try again.
 */
function wording(status: number | null): string {
  if (status === null) {
    return 'The upload could not reach our storage. Check your connection and try again.'
  }
  if (status === 403) {
    return 'That upload link is no longer valid. Go back and start again.'
  }
  if (status === 413) {
    return 'That file is too large to upload.'
  }
  return 'Our storage would not accept that file. Try again in a moment.'
}

/**
 * Sends the body, retrying a failure that might not happen twice.
 *
 * `send` is injected so a test can fail the first attempt without a network.
 */
export async function putWithRetry(
  url: string | undefined,
  body: Blob | File,
  send: typeof fetch = fetch,
): Promise<void> {
  if (!url) throw new ApiError(500, 'The upload could not be started.')

  let status: number | null = null

  for (let attempt = 0; attempt < TRIES; attempt++) {
    // A hung request never throws on its own, so the retry below would never
    // be reached without this.
    const stop = new AbortController()
    const timer = setTimeout(() => stop.abort(), GIVE_UP_AFTER)
    try {
      /*
       * No header of ours goes with it. The presigned link carries its own
       * authorisation and sending one would break the signature.
       */
      const res = await send(url, { method: 'PUT', body, signal: stop.signal })
      if (res.ok) return
      status = res.status
    } catch {
      // A network failure, or our own timeout. Either way there is no answer.
      status = null
    } finally {
      clearTimeout(timer)
    }

    if (!worthRetrying(status)) break
    if (attempt < TRIES - 1) await sleep(WAITS[attempt] ?? 1200)
  }

  throw new ApiError(status ?? 0, wording(status))
}
